import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useForgotPasswordMutation, useResetPasswordMutation, useValidateResetPasswordCodeMutation } from "../../../../../../service/authentication-service";
import { FadeSlideSwitcher } from "../../../../../../common/components/fade-slide-switcher/FadeSlideSwitcher";
import { ButtonWithLoadingState } from "../../../../../../common/components/button-with-loading-state/ButtonWithLoadingState";
import { CountdownTextHandle } from "../../../../../../common/components/countdown-text/CountdownText";
import { OtpVerificationCard } from "../../../../../../common/components/otp-verification-card/OtpVerificationCard";
import { maskEmail } from "../../../../onboarding/forgot-password/util";
import { getErrorMessage } from "../../../../../../utils/error-handler";
import { RootState } from "../../../../../../stores/store";
import { StepSuccessCard } from "../../../../../../common/components/step-success-card/StepSuccessCard";
import { CloseXIcon, PasswordEyeOpenIcon, PasswordEyeHiddenIcon, CheckmarkCircleLgIcon } from "../../../../../../assets/icons";
import styles from "./ResetPasswordModal.module.scss";

// Shorthand for bracket-notation access on kebab/BEM class names
const s = styles as Record<string, string>;

type Step = "initial" | "emailSent" | "codeValidated" | "passwordReset";

// ── NewPasswordCard ───────────────────────────────────────────────────────────

interface NewPasswordCardProps {
	password: string;
	confirmPassword: string;
	onPasswordChange: (v: string) => void;
	onConfirmChange: (v: string) => void;
	error?: string;
}

const EyeIcon: React.FC<{ visible: boolean }> = ({ visible }) => (visible ? <PasswordEyeOpenIcon /> : <PasswordEyeHiddenIcon />);

const NewPasswordCard: React.FC<NewPasswordCardProps> = ({ password, confirmPassword, onPasswordChange, onConfirmChange, error }) => {
	const { t } = useTranslation();
	const [showPw, setShowPw] = useState(false);
	return (
		<div className={s["rp-card"]}>
			<span className={s["rp-card__title"]}>{t("account.web.createNewPassword")}</span>
			<p className={s["rp-card__subtitle"]}>{t("account.web.chooseStrongPasswordForAccount")}</p>
			<div className={s["rp-field"]}>
				<label className={s["rp-label"]}>{t("common.labels.newPassword")}</label>
				<div className={s["rp-input-wrap"]}>
					<input
						className={`${s["rp-input"]} ${s["rp-input--inline"]}`}
						type={showPw ? "text" : "password"}
						placeholder={t("account.web.newPasswordPlaceholder")}
						value={password}
						onChange={e => onPasswordChange(e.target.value)}
						autoComplete="new-password"
					/>
					<button type="button" className={s["rp-eye-btn"]} onClick={() => setShowPw(v => !v)} tabIndex={-1} aria-label={t("account.web.togglePasswordVisibility")}>
						<EyeIcon visible={showPw} />
					</button>
				</div>
			</div>
			<div className={s["rp-field"]}>
				<label className={s["rp-label"]}>{t("common.labels.confirmPassword")}</label>
				<div className={s["rp-input-wrap"]}>
					<input
						className={`${s["rp-input"]} ${s["rp-input--inline"]}`}
						type={showPw ? "text" : "password"}
						placeholder={t("account.web.confirmPasswordPlaceholder")}
						value={confirmPassword}
						onChange={e => onConfirmChange(e.target.value)}
						autoComplete="new-password"
					/>
				</div>
			</div>
			{error && <p className={s["rp-error"]}>{error}</p>}
			<hr className={s["rp-divider"]} />
			<span className={s["rp-requirements__title"]}>{t("common.labels.passwordRequirementsTitle")}</span>
			<p className={s["rp-requirements__body"]}>{t("account.web.passwordRequirementsBody")}</p>
		</div>
	);
};

// ── FinalStepSuccessCard ──────────────────────────────────────────────────────

interface FinalStepSuccessCardProps {
	title: string;
	description?: string;
}

const FinalStepSuccessCard: React.FC<FinalStepSuccessCardProps> = ({ title, description }) => (
	<div className={`${s["rp-card"]} ${s["rp-card--success-final"]}`}>
		<CheckmarkCircleLgIcon />
		<span className={s["rp-success-title"]}>{title}</span>
		{description && <p className={s["rp-success-desc"]}>{description}</p>}
	</div>
);

// ── ResetPasswordModal ────────────────────────────────────────────────────────

export interface ResetPasswordModalProps {
	onClose: (success?: boolean) => void;
}

export const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({ onClose }) => {
	const { t } = useTranslation();
	const email = useSelector((state: RootState) => state.user.email ?? "");

	const [step, setStep] = useState<Step>("initial");
	const [otpCode, setOtpCode] = useState("");
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [pwError, setPwError] = useState<string | undefined>();
	const countdownRef = useRef<CountdownTextHandle>(null);

	const [forgotPassword, { isLoading: isSendingOtp }] = useForgotPasswordMutation();
	const [validateCode, { isLoading: isValidating }] = useValidateResetPasswordCodeMutation();
	const [resetPassword, { isLoading: isResetting }] = useResetPasswordMutation();

	useEffect(() => {
		forgotPassword(email)
			.unwrap()
			.then(() => setStep("emailSent"))
			.catch((err: any) => {
				toast.error(getErrorMessage(err?.data));
				onClose();
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (step === "emailSent") {
			countdownRef.current?.startOtpResendCodeTimer();
		}
	}, [step]);

	const handleResend = async () => {
		try {
			await forgotPassword(email).unwrap();
			toast.success(t("account.web.codeResentSuccessfully"));
		} catch (err: any) {
			toast.error(getErrorMessage(err?.data));
		}
	};

	const handleContinue = async () => {
		if (step === "emailSent") {
			try {
				await validateCode({ token: otpCode, email }).unwrap();
				setStep("codeValidated");
			} catch (err: any) {
				toast.error(getErrorMessage(err?.data));
			}
		} else if (step === "codeValidated") {
			if (password.length < 8) {
				setPwError(t("account.web.passwordMinLength8"));
				return;
			}
			if (password !== confirmPassword) {
				setPwError(t("common.validation.passwordsDoNotMatch"));
				return;
			}
			setPwError(undefined);
			try {
				await resetPassword({ token: otpCode, password, email }).unwrap();
				setStep("passwordReset");
			} catch (err: any) {
				toast.error(getErrorMessage(err?.data));
			}
		} else if (step === "passwordReset") {
			onClose(true);
		}
	};

	const isLoading = isSendingOtp || isValidating || isResetting;
	const buttonLabel = step === "passwordReset" ? t("account.web.close") : t("common.actions.continueKey");
	const buttonDisabled = step === "initial" || (step === "emailSent" && otpCode.trim() === "") || isLoading;

	return (
		<div className={s["rp-overlay"]} onClick={() => onClose()}>
			<div className={s["rp-sheet"]} onClick={e => e.stopPropagation()}>
				<div className={s["rp-header"]}>
					<button type="button" className={s["rp-close-btn"]} onClick={() => onClose()} aria-label={t("account.web.close")}>
						<CloseXIcon />
					</button>
					<span className={s["rp-header__title"]}>{t("common.labels.changePassword")}</span>
				</div>
				<hr className={s["rp-header__divider"]} />

				<div className={s["rp-body"]}>
					<FadeSlideSwitcher switchKey={step}>
						<div className={s["rp-step-content"]}>
							{step === "initial" && (
								<div className={`${s["rp-card"]} ${s["rp-card--loading"]}`}>
									<span className={s["rp-card__title"]}>{t("auth.otpVerification")}</span>
									<div className={`${s["rp-skeleton"]} ${s["rp-skeleton--text"]}`} />
									<div className={s["rp-loader-row"]}>
										<span className={`${s["rp-spinner"]} ${s["rp-spinner--sm"]}`} />
										<span className={s["rp-loader-row__text"]}>{t("account.web.sendingCodeToYourEmail")}</span>
									</div>
								</div>
							)}

							{step === "emailSent" && (
								<OtpVerificationCard
									title={t("auth.otpVerification")}
									subtitle={
										<>
											{t("auth.enterOtpSentToEmail")} <span className="emailAccent">"{maskEmail(email)}"</span>
										</>
									}
									value={otpCode}
									onChange={setOtpCode}
									isVerifying={isValidating}
									countdownRef={countdownRef}
									onResend={handleResend}
								/>
							)}

							{step === "codeValidated" && (
								<div className={s["rp-stack"]}>
									<StepSuccessCard text={t("auth.otpVerified")} />
									<NewPasswordCard
										password={password}
										confirmPassword={confirmPassword}
										onPasswordChange={v => {
											setPassword(v);
											setPwError(undefined);
										}}
										onConfirmChange={v => {
											setConfirmPassword(v);
											setPwError(undefined);
										}}
										error={pwError}
									/>
								</div>
							)}

							{step === "passwordReset" && (
								<FinalStepSuccessCard title={t("account.web.passwordResetSuccessfully")} description={t("auth.proceedToSignin")} />
							)}
						</div>
					</FadeSlideSwitcher>
				</div>

				<div className={s["rp-footer"]}>
					<ButtonWithLoadingState buttonText={buttonLabel} loading={isLoading && step !== "initial"} buttonPressed={buttonDisabled ? null : handleContinue} />
				</div>
			</div>
		</div>
	);
};

export default ResetPasswordModal;
